import { ConditionalLink } from "components/conditionalLink";
import { Divider } from "components/design/Divider";
import Card from "components/DesignSystem/Card/Card";
import { BareInput } from "components/DesignSystem/TextInput/TextInput";
import { FileInput } from "components/FileInput/FileInput";
import { Redirect } from "components/icons/Redirect";
import { LocalFilePreview, Preview } from "components/PreviewModal";
import {
  CHECKLIST_ITEM_SECTION,
  CHECKLIST_ITEM_TYPE,
  CLOSING_STATUS,
} from "constants/financialClosing";
import { FINANCIAL_CLOSING_CHECKLIST_LABEL } from "dictionaries";
import { useFormikContext } from "formik";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useCallback } from "react";
import {
  ChecklistItem,
  FinancialClosingDetail,
} from "store/apis/financialClosing";
import { debounce } from "utils/debouncing";
import { ActionItem, CheckListUpdatePayload, getStatus } from "./CheckList";

const FinalChecklistReviewPendingLabel = ({
  isFinalizeFinancialsDisabled,
  item,
  onCheckListChange,
  setFieldValue,
  index,
  currentIndex,
}: {
  isFinalizeFinancialsDisabled: boolean;
  item: ChecklistItem;
  onCheckListChange: (payload: CheckListUpdatePayload) => void;
  setFieldValue: (field: string, value: any) => void;
  index: number;
  currentIndex: number;
}) => {
  const { downloadFile } = useDownloadFile();
  const previewModal = useModal();

  const link_titles = [
    "View Income Statement",
    "View Cash Flow Statement",
    "View Balance Sheet",
  ];

  const getFinalizeFinancialsLabel = useCallback((type: string) => {
    switch (type) {
      case CHECKLIST_ITEM_TYPE.REVIEW_BALANCE_SHEET:
        return "Balance Sheet";
      case CHECKLIST_ITEM_TYPE.REVIEW_INCOME_STATEMENT:
        return "Income Statement";
      case CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT:
        return "Cash Flow Statement";
      default:
        return "";
    }
  }, []);

  const file_data = {
    file_id: item?.file_data?.uuid!,
    group_id: item?.file_data?.group_id!,
  };

  return (
    <>
      <section>
        {isFinalizeFinancialsDisabled ? (
          <section className="t-flex t-flex-col t-gap-1.5 t-p-4 hover:t-bg-surface-lighter-grey t-w-full t-border-b t-border-0 t-border-solid t-border-neutral-0 ">
            <section>
              <span className="t-text-subtext-sm">
                {getFinalizeFinancialsLabel(item.type)}
              </span>
            </section>
            <section>
              {item?.file_url ? (
                <ConditionalLink
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  to={item?.file_url}
                  target="_blank"
                >
                  <span className="t-text-purple-70 t-text-body-sm t-font-medium t-no-underline hover:t-underline">
                    {link_titles[currentIndex]}
                  </span>
                </ConditionalLink>
              ) : (
                <FileInput
                  file={
                    item.file_data
                      ? {
                          name: item.file_data.name,
                          file_type:
                            item.file_data.file_type || item.file_data.type,
                          is_previewable: item.file_data.is_previewable,
                          uuid: item.file_data.uuid,
                        }
                      : undefined
                  }
                  onFileClick={previewModal.open}
                  onDrop={debounce((file) => {
                    onCheckListChange({
                      checklistItemId: item.uuid,
                      file_data: file[0] as File,
                    });
                    setFieldValue(
                      `checklist_items[${index}].file_data`,
                      file[0] as File
                    );
                  })}
                  onDownload={downloadFile.bind(null, file_data)}
                />
              )}
            </section>
          </section>
        ) : (
          <section className="t-flex t-flex-col t-gap-1.5">
            <section>
              <span className="t-text-subtext-sm">
                {FINANCIAL_CLOSING_CHECKLIST_LABEL[item.type]}
              </span>
              <span className="after:t-font-bold after:t-text-red after:t-content-['_*']" />
            </section>
            <section>
              {item?.file_url ? (
                <ConditionalLink
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  to={item?.file_url}
                  target="_blank"
                >
                  <span className="t-text-purple-70 t-text-body-sm t-font-medium t-no-underline hover:t-underline">
                    {link_titles[currentIndex]}
                  </span>
                </ConditionalLink>
              ) : (
                <FileInput
                  file={
                    item.file_data
                      ? {
                          name: item.file_data.name,
                          file_type:
                            item.file_data.file_type || item.file_data.type,
                          is_previewable: item.file_data.is_previewable,
                          uuid: item.file_data.uuid,
                        }
                      : undefined
                  }
                  onFileClick={previewModal.open}
                  onDrop={debounce((file) => {
                    onCheckListChange({
                      checklistItemId: item.uuid,
                      file_data: file[0] as File,
                    });
                    setFieldValue(
                      `checklist_items[${index}].file_data`,
                      file[0] as File
                    );
                  })}
                  onDownload={downloadFile.bind(null, file_data)}
                />
              )}
            </section>
          </section>
        )}
      </section>
      <Preview
        showModal={previewModal.isOpen}
        closeModal={previewModal.close}
        previewId={item.file_data?.uuid}
      />
    </>
  );
};

const FinancialClosingCheckList = ({
  item,
  index,
  onCheckListChange,
  getReportLinks,
  isFormEditable,
}: {
  item: ChecklistItem;
  index: number;
  onCheckListChange: (arg: CheckListUpdatePayload) => Promise<void>;
  getReportLinks: (type: ChecklistItem["type"]) => string;
  isFormEditable: boolean;
}) => {
  const { setFieldValue } = useFormikContext<{
    checklist_items: ChecklistItem[];
  }>();

  const previewModal = useModal();
  const { downloadFile } = useDownloadFile();

  return (
    <Card.Root>
      <div className="t-flex t-flex-col t-gap-4 t-py-4 t-w-full">
        <div className="t-text-text-60 t-text-subtext-sm t-flex t-justify-between t-w-full">
          <span>
            {FINANCIAL_CLOSING_CHECKLIST_LABEL[item.type]}
            <span className="after:t-font-bold after:t-text-red after:t-content-['_*']" />
          </span>
          <ConditionalLink
            onClick={(e) => {
              e.stopPropagation();
            }}
            to={getReportLinks(item.type)}
            target="_blank"
            className="t-text-text-30 hover:t-text-purple"
          >
            <Redirect size={16} />
          </ConditionalLink>
        </div>
        <FileInput
          label="Add"
          name={`checklist_items[${index}].file_data`}
          disabled={!isFormEditable && !item.file_data}
          file={
            item.file_data
              ? {
                  name: item.file_data.name,
                  file_type: item.file_data.file_type || item.file_data.type,
                  is_previewable: item.file_data.is_previewable,
                  uuid: item.file_data.uuid,
                }
              : undefined
          }
          {...(isFormEditable && {
            onDelete: () => {
              onCheckListChange({
                checklistItemId: item.uuid,
                file_data: undefined,
              });
              setFieldValue(`checklist_items[${index}].file_data`, undefined);
            },
          })}
          onFileClick={previewModal.open}
          onDrop={debounce((file) => {
            onCheckListChange({
              checklistItemId: item.uuid,
              file_data: file[0] as File,
            });
            setFieldValue(
              `checklist_items[${index}].file_data`,
              file[0] as File
            );
          })}
          onDownload={
            item.file_data?.uuid && item.file_data?.group_id
              ? downloadFile.bind(null, {
                  file_id: item.file_data.uuid,
                  group_id: item.file_data.group_id,
                })
              : undefined
          }
        />
        <Divider color="dark-grey">OR</Divider>
        <BareInput
          defaultValue={item.file_url}
          onChange={debounce((e) => {
            const value = e.target.value;
            onCheckListChange({
              checklistItemId: item.uuid,
              file_url: value,
            });
            setFieldValue(
              `checklist_items[${index}].file_url`,
              !item.resolved_by_admin
            );
          })}
          label="Provide a URL to an external document"
          placeholder="Add link"
          disabled={!isFormEditable}
        />
      </div>
      {item.file_data?.uuid && (
        <Preview
          showModal={previewModal.isOpen}
          closeModal={previewModal.close}
          previewId={item.file_data?.uuid}
        />
      )}

      {!item.file_data?.uuid && previewModal.isOpen && (
        <LocalFilePreview
          isOpen={previewModal.isOpen}
          close={previewModal.close}
          // @ts-ignore
          file={item?.file_data}
        />
      )}
    </Card.Root>
  );
};

export default function FinalizeFinancials({
  closingDetails,
  checklistItems,
  onCheckListChange,
  isFormEditable,
}: {
  checklistItems: ChecklistItem[];
  closingDetails: FinancialClosingDetail;
  onCheckListChange: (payload: CheckListUpdatePayload) => Promise<void>;
  isFormEditable: boolean;
}) {
  const { link } = useConstructInternalLink();
  const { isCpa, isCustomer } = useRoleBasedView();
  const { start_date, end_date } = closingDetails;
  const { setFieldValue } = useFormikContext<{
    checklist_items: ChecklistItem[];
  }>();

  const finalizeFinancials = checklistItems.filter(
    ({ section }) => section === CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS
  );

  const getIndex = (uuid: string) => {
    return checklistItems.findIndex((item) => item.uuid === uuid);
  };

  const isFinalizeFinancialsDisabled =
    closingDetails?.status === CLOSING_STATUS.COMPLETED ||
    (closingDetails?.status === CLOSING_STATUS.REVIEW_PENDING && isCpa);

  const getReportLinks = (type: string) => {
    const REPORT_LINK = {
      [CHECKLIST_ITEM_TYPE.REVIEW_BALANCE_SHEET]: "/books/live-financials",
      [CHECKLIST_ITEM_TYPE.REVIEW_INCOME_STATEMENT]:
        "/books/live-financials/income-statement",
      [CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT]:
        "/books/live-financials/cash-flow-statement",
    };

    return link(REPORT_LINK[type as keyof typeof REPORT_LINK], {
      moreQuery: {
        startDate: start_date,
        endDate: end_date,
      },
    });
  };

  return (
    <>
      {finalizeFinancials.map((item, i) => {
        const index = getIndex(item.uuid);

        return closingDetails?.status !== CLOSING_STATUS.IN_PROGRESS ? (
          <section>
            {!isFinalizeFinancialsDisabled ? (
              <ActionItem
                // name={`checklist_items[${index}].file_data`}
                disabled={false}
                key={index}
                label={
                  <FinalChecklistReviewPendingLabel
                    isFinalizeFinancialsDisabled={isFinalizeFinancialsDisabled}
                    item={item}
                    onCheckListChange={onCheckListChange}
                    setFieldValue={setFieldValue}
                    index={index}
                    currentIndex={i}
                  />
                }
                description={item.checklist_item_status}
                status={getStatus({
                  resolved_by_system: item.resolved_by_system,
                  resolved_by_admin: item.resolved_by_admin,
                })}
                onIconClick={debounce(() => {
                  if (!isCustomer) return;
                  onCheckListChange({
                    resolved_by_admin: !item.resolved_by_admin,
                    checklistItemId: item.uuid,
                  });
                  setFieldValue(
                    `checklist_items[${index}].resolved_by_admin`,
                    !item.resolved_by_admin
                  );
                })}
              />
            ) : (
              <FinalChecklistReviewPendingLabel
                isFinalizeFinancialsDisabled={true}
                item={item}
                onCheckListChange={onCheckListChange}
                setFieldValue={setFieldValue}
                index={index}
                currentIndex={i}
              />
            )}
          </section>
        ) : (
          <FinancialClosingCheckList
            key={item.uuid}
            index={index}
            item={item}
            getReportLinks={getReportLinks}
            onCheckListChange={onCheckListChange}
            isFormEditable={isFormEditable}
          />
        );
      })}
    </>
  );
}
